// NOTE: NSAI-1606 — validation for the decoded GET /app-config.
//
// The payload is untrusted input: the mobile app can be pointed at any server,
// so whatever a server says about itself is checked here, once, at the decode
// boundary. Every field carries its own fallback, so one value that does not
// fit is replaced on its own instead of taking the rest of the response with
// it, and every client reads a complete object. The fallbacks lean towards
// hiding a surface rather than offering one the server would refuse: a switch
// reads as off, a list as empty, and an unreadable on-premise flag as
// on-premise. Three rules from the multi-tenant design hold throughout: only
// https URLs survive, an unknown flavor reads as the default brand, and only a
// PostHog project key is ever handed to the SDK.

import { z } from "zod";

import { APP_CONFIG_DEFAULT_FLAVOR } from "../constants/app-config-defaults";
import { DEFAULT_MAX_FILE_UPLOAD_COUNT } from "../constants/chat";
import {
  EServerApiCapability,
  type IAppConfigResponse,
} from "../types/app-config";
import { ESocialAuthProvider } from "../types/auth";
import { EPushPlatform } from "../types/notifications";
import { isRecord } from "../utils/type-guards";

// NOTE: Major.minor.patch only — the mobile version gate compares numbers, and
// a pre-release suffix it cannot order is better ignored than guessed at.
const SEMVER_PATTERN = /^\d+\.\d+\.\d+$/;

// NOTE: NSAI-5890 — a base a path is appended to, so a query or a fragment
// would end up in the middle of every link built on it.
const URL_QUERY_OR_FRAGMENT_PATTERN = /[?#]/;

// NOTE: NSAI-5884 — a PostHog project key can only write events; anything
// else (a personal API key, a garbled value) is never handed to the SDK.
const POSTHOG_PROJECT_KEY_PREFIX = "phc_";

const SOCIAL_AUTH_PROVIDERS: ReadonlySet<string> = new Set(
  Object.values(ESocialAuthProvider),
);

const isSocialAuthProvider = (value: unknown): value is ESocialAuthProvider =>
  typeof value === "string" && SOCIAL_AUTH_PROVIDERS.has(value);

const SERVER_API_CAPABILITIES: ReadonlySet<string> = new Set(
  Object.values(EServerApiCapability),
);

const isServerApiCapability = (value: unknown): value is EServerApiCapability =>
  typeof value === "string" && SERVER_API_CAPABILITIES.has(value);

const PUSH_PLATFORMS: ReadonlySet<string> = new Set(
  Object.values(EPushPlatform),
);

const isPushPlatform = (value: unknown): value is EPushPlatform =>
  typeof value === "string" && PUSH_PLATFORMS.has(value);

// NOTE: A group that is missing or not an object decodes as an empty one, so
// each of its fields falls back on its own.
const group = <TShape extends z.ZodRawShape>(
  shape: TShape,
): z.ZodPreprocess<z.ZodObject<TShape>> =>
  z.preprocess((value) => (isRecord(value) ? value : {}), z.object(shape));

const flag = (fallback = false): z.ZodCatch<z.ZodBoolean> =>
  z.boolean().catch(fallback);

const optionalText = z.string().min(1).optional().catch(undefined);

const textList = z
  .array(z.unknown())
  .catch([])
  .transform((values) =>
    values.filter(
      (value): value is string => typeof value === "string" && value !== "",
    ),
  );

const httpsUrlSchema = z
  .string()
  .url()
  .refine((value) => value.startsWith("https://"));

const optionalHttpsUrl = httpsUrlSchema.optional().catch(undefined);

const flavorSchema = z
  .object({
    key: z.enum(["nodeshift", "oplot"]).catch(APP_CONFIG_DEFAULT_FLAVOR.key),
    appName: z.string().min(1).catch(APP_CONFIG_DEFAULT_FLAVOR.appName),
    companyName: z.string().min(1).catch(APP_CONFIG_DEFAULT_FLAVOR.companyName),
    defaultLanguage: z
      .string()
      .min(1)
      .catch(APP_CONFIG_DEFAULT_FLAVOR.defaultLanguage),
    logoUrl: optionalHttpsUrl,
  })
  .catch(APP_CONFIG_DEFAULT_FLAVOR);

// NOTE: NSAI-5890 — the whole entry goes when its base is unusable, so a
// client never holds a `links` without a `shareBaseUrl` and falls back to its
// own origin, exactly as with a server that sends none.
const linksSchema = z
  .object({
    shareBaseUrl: httpsUrlSchema.refine(
      (value) => !URL_QUERY_OR_FRAGMENT_PATTERN.test(value),
    ),
  })
  .optional()
  .catch(undefined);

const aiProviderSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  isSelfHosted: z.boolean(),
});

// NOTE: The root goes through `group` too, so a response that is not an object
// at all still decodes to the conservative defaults.
export const appConfigSchema = group({
  deployment: group({
    // NOTE: An unreadable answer reads as on-premise — the direction that hides
    // the cloud-only surfaces (billing, social sign-in, telemetry).
    isOnPremise: flag(true),
    // NOTE: NSAI-7701 — kept optional: a value that is not a boolean reads as
    // "unknown", and the mobile app then behaves as it did before the field.
    isClosedNetwork: z.boolean().optional().catch(undefined),
    isBillingDisabled: flag(true),
    isDomainVerificationUnavailable: flag(),
    flavor: flavorSchema,
    // NOTE: NSAI-7414 — a value from a newer server that this client cannot
    // name is dropped on its own; both directions hide a surface rather than
    // offer one that fails.
    apiCapabilities: z
      .array(z.unknown())
      .catch([])
      .transform((values) => values.filter(isServerApiCapability)),
    // NOTE: NSAI-7701 — a platform this client cannot name is dropped on its
    // own; a value that is not even a list reads as "unknown" (undefined, not
    // an empty list), and the app keeps asking for notification permission.
    pushPlatforms: z
      .array(z.unknown())
      .optional()
      .catch(undefined)
      .transform((values) => values?.filter(isPushPlatform)),
    minMobileAppVersion: z
      .string()
      .regex(SEMVER_PATTERN)
      .optional()
      .catch(undefined),
    links: linksSchema,
  }),
  auth: group({
    isSsoVisible: flag(),
    isPasswordAuthDisabled: flag(),
    isSignUpEnabled: flag(),
    socialProviders: z
      .array(z.unknown())
      .catch([])
      .transform((values) => values.filter(isSocialAuthProvider)),
    isPasswordResetSelfService: flag(),
    isSamlSeatTiersEnabled: flag(),
  }),
  features: group({
    isAgentsEnabled: flag(),
    isSkillsEnabled: flag(),
    isSkillScriptExecutionAvailable: flag(),
    isCustomIntegrationEnabled: flag(),
    isCodeSessionsEnabled: flag(),
    isExternalIntegrationsProviderEnabled: flag(),
    isDocRecognitionEnabled: flag(),
    isBrandedPresentationsEnabled: flag(),
    isUserMemoryEnabled: flag(),
    isZipArchiveUploadEnabled: flag(),
    isAdminPanelEnabled: flag(),
    isModelApprovalEnabled: flag(),
    isSyncatorApiEnabled: flag(),
    isMobileChatSearchEnabled: flag(),
    isTokenUsageEnabled: flag(),
    isTokenCostUsageEnabled: flag(),
    isUserAnalyticsEnabled: flag(),
    isTokenLimitsEnabled: flag(),
    isCallAssistantFeaturesEnabled: flag(),
    isCallAssistantFeaturesEnabledFF: flag(),
    isAnonymizationLabEnabled: flag(),
    isAnonymizationLabEnabledFF: flag(),
    isModelSubscriptionConnectionsEnabled: flag(),
    isModelSubscriptionConnectionsEnabledFF: flag(),
    isFinancialMaskingFeatureEnabled: flag(),
    isPiiCustomEntitiesFeatureEnabled: flag(),
    isPiiAdvancedRulesFeatureEnabled: flag(),
    isPiiIntentRoutingFeatureEnabled: flag(),
    isSiemExportEnabled: flag(),
    isSiemGlobalCollectorActive: flag(),
  }),
  bots: group({
    isTelegramBotEnabled: flag(),
    isWhatsappBotEnabled: flag(),
    isWhatsappBotEnabledFF: flag(),
    isMatrixBotEnabled: flag(),
    // NOTE: Both are rendered as links a user opens, so they follow the same
    // https-only rule as every other URL here.
    slackBotUri: optionalHttpsUrl,
    teamsBotUri: optionalHttpsUrl,
  }),
  meetings: group({
    isKonturTalkEnabled: flag(),
    isYandexTelemostEnabled: flag(),
    isSaluteJazzEnabled: flag(),
    jitsiCustomDomains: textList,
    enabledCalendars: textList,
    // NOTE: NSAI-7701 — surrounding whitespace is not part of an address.
    meetingBotEmail: z.string().trim().min(1).optional().catch(undefined),
  }),
  audio: group({
    isAudioTranscriptionEnabled: flag(),
    isExternalServiceForAudioTranscription: flag(),
    isTtsEnabled: flag(),
    isExternalServiceForTts: flag(),
    ttsVoices: textList,
  }),
  // NOTE: NSAI-5934 — only a string URL and a numeric zoom are taken; anything
  // else reads as absent, which the map answers by leaving the basemap off.
  map: group({
    tileUrl: optionalText,
    tileAttribution: optionalText,
    maxZoom: z.number().optional().catch(undefined),
  }),
  limits: group({
    maxFileUploadCount: z
      .number()
      .int()
      .positive()
      .catch(DEFAULT_MAX_FILE_UPLOAD_COUNT),
    // NOTE: NSAI-7817 — only a positive number is a usable cap; anything else
    // leaves the client's pre-check off (the server still enforces it).
    knowledgeMaxFileSizeMb: z.number().positive().optional().catch(undefined),
  }),
  analytics: group({
    isEnabled: flag(),
    posthogKey: z
      .string()
      .startsWith(POSTHOG_PROJECT_KEY_PREFIX)
      .optional()
      .catch(undefined),
    posthogHost: optionalHttpsUrl,
  }),
  legal: group({
    supportEmail: z.string().email().optional().catch(undefined),
    privacyPolicyUrl: optionalHttpsUrl,
    termsUrl: optionalHttpsUrl,
  }),
  policies: group({
    isAccountDeletionEnabled: flag(),
  }),
  ai: group({
    // NOTE: One entry that does not fit is dropped on its own.
    providers: z
      .array(z.unknown())
      .catch([])
      .transform((values) =>
        values.flatMap((value) => {
          const parsed = aiProviderSchema.safeParse(value);

          return parsed.success ? [parsed.data] : [];
        }),
      ),
  }),
});

// NOTE: Never throws: every field has a fallback, so even `undefined` decodes
// to a complete, conservative configuration.
export const parseAppConfig = (value: unknown): IAppConfigResponse =>
  appConfigSchema.parse(value) satisfies IAppConfigResponse;
